import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import axiosInstance from '../axiosConfig';
import KanbanBoard from '../components/KanbanBoard';

const KnowledgeDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [knowledgeData, setKnowledgeData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [activeTab, setActiveTab] = useState('knowledge');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    category: 'frontend',
    topic: 'improvement',
    problem: '',
    solution: '',
    codeExample: '',
    jobId: '',
    requestDate: new Date().toISOString().split('T')[0],
    impact: '',
    approvalStatus: 'pending',
    errorLogs: {
      admin: '',
      database: '',
      docker: ''
    }
  });

  // Carregar dados da base de conhecimento
  useEffect(() => {
    // Simulando carregamento
    setTimeout(() => {
      // Dados estáticos da base de conhecimento
      const knowledgeBaseData = [
        {
          id: 36,
          title: 'Correção de Exibição de Dias da Semana em Cenários Salvos',
          category: 'Correção de Bug',
          topic: 'Cenários e Simulações',
          description: 'Correção do problema onde a coluna "DIAS/SEMANA" sempre exibia o valor 5, independentemente da configuração do usuário.',
          problem: `
Na versão anterior do sistema, a tabela de cenários salvos na página de Relatórios exibia incorretamente o valor fixo "5" para todos os cenários na coluna "DIAS/SEMANA", independentemente do valor que o usuário tinha configurado durante a criação do cenário. Isso gerava confusão, já que os usuários perdiam a referência da configuração original e os resultados calculados não correspondiam ao número de dias exibido.

O problema ocorria porque:
1. A API do backend não incluía o campo "days_per_week" nos atributos retornados
2. O frontend não acessava corretamente este valor do modelo de dados
3. Não havia consistência entre o valor usado no cálculo e o valor exibido na interface
    `,
          solution: `
A solução implementada envolve modificações tanto no backend quanto no frontend:

1. **Backend**: 
   - Modificado o método "listSimulations" no controlador "simulationController.js" para incluir explicitamente o atributo "days_per_week" na resposta da API
   - Implementado sistema de logs para registrar inconsistências de dados

2. **Frontend**:
   - Adicionada coluna "DIAS/SEMANA" à tabela de cenários salvos com valor dinâmico
   - Implementado tooltip para melhorar a clareza da informação
   - Adicionado ícone de informação para indicar ao usuário que pode obter mais detalhes

3. **Validação**:
   - Adicionada validação para garantir que o valor de "days_per_week" esteja entre 1 e 7
   - Implementado registro de inconsistências quando o valor é alterado
    `,
          codeExample: `
// Backend - Controlador de Simulações
static async listSimulations(req, res) {
  try {
    const { user_id } = req.params;
    const { status, is_executed } = req.query;
    
    const where = { user_id };
    
    if (status) {
      where.status = status;
    }
    
    if (is_executed !== undefined) {
      where.is_executed = is_executed === 'true';
    }
    
    const simulations = await Simulation.findAll({
      where,
      attributes: [
        'id', 
        'name', 
        'hash_id', 
        'initial_capital', 
        'days_per_week', // Garantir que retornamos os dias da semana
        'profit_percentage', 
        'start_date', 
        'end_date', 
        'status', 
        'is_executed',
        'execution_date',
        'created_at'
      ],
      order: [['created_at', 'DESC']]
    });
    
    return res.status(200).json(simulations);
  } catch (error) {
    // Registrar erro no log
    await LogService.logApiError(
      req.user?.id,
      '/simulations',
      'GET',
      error,
      { user_id: req.params.user_id }
    );
    
    return res.status(500).json({ error: 'Erro ao listar simulações', details: error.message });
  }
}

// Frontend - Componente da Tabela de Cenários
<td>
  <div className="d-flex align-items-center">
    <span 
      data-bs-toggle="tooltip" 
      data-bs-placement="top" 
      title={\`Esta simulação opera em \${simulation.days_per_week} dias por semana\`}
    >
      {simulation.days_per_week}
    </span>
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      className="icon text-muted ms-1" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      strokeWidth="2" 
      stroke="currentColor" 
      fill="none" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
      <polyline points="11 12 12 12 12 16 13 16" />
    </svg>
  </div>
</td>
    `,
          readmeLink: "https://github.com/organizacao/plataforma-investimento/docs/dias_semana.md",
          date: "2025-05-24",
          authorName: "Equipe de Desenvolvimento",
          authorRole: "Dev Team",
          tags: ["bug-fix", "frontend", "backend", "data-integrity", "dias-semana", "simulação"]
        },
        {
          id: 35,
          title: 'Implementação de Controle para Execução de Cenários com ID-HASH',
          category: 'Desenvolvimento',
          topic: 'Alterações em Simulações',
          description: 'Documentação detalhada sobre a implementação de um sistema que impede a execução repetida de cenários de simulação, utilizando ID-HASH único para preservar a integridade dos dados e permitir rastreabilidade.',
          problem: `
Problema: As simulações financeiras podiam ser executadas múltiplas vezes com o mesmo ID, o que:
1. Permitia sobrescrever dados históricos, perdendo rastreabilidade
2. Não preservava as condições exatas (cotações de moedas, índices) do momento da execução
3. Dificultava a auditoria e verificação dos resultados
4. Não havia versionamento claro entre diferentes execuções do mesmo cenário

Era necessário implementar um mecanismo que:
- Garantisse execução única por ID-HASH
- Permitisse clonar um cenário para reutilizá-lo
- Fornecesse identificação visual clara do status (executado/pendente)
- Mantivesse registro das datas de execução`,
          solution: `
Solução: Foi implementado um sistema completo de controle de execução de cenários com diversas camadas:

1. Banco de Dados:
   - Adição dos campos 'hash_id', 'execution_date', 'is_executed' e 'cloned_from' à tabela 'simulations'
   - Adição do campo 'name' para melhor identificação dos cenários

2. Backend:
   - Nova API para execução de cenários que verifica o status antes de permitir a execução
   - Implementação de geração de hash_id único com timestamp e string aleatória
   - API para clonagem de cenários com novos identificadores
   - Validação para impedir execuções repetidas

3. Frontend:
   - Nova visualização em tabela dos cenários salvos
   - Distinção visual entre cenários executados (Feito) e pendentes
   - Botão de "Executar" substituído por "Feito" após a execução
   - Modal de clonagem para criar cópias de cenários
   - Informações visuais de data de execução e hash ID

Esta implementação garante a integridade dos dados históricos e fornece rastreabilidade completa das simulações.`,
          codeExample: `
// 1. Migração para adicionar os campos necessários
// backend/src/migrations/20250501000001-add-execution-date-hash-to-simulations.js
'use strict';

module.exports = {
  up: async (queryInterface, Sequelize) => {
    await queryInterface.sequelize.transaction(async (transaction) => {
      // Adicionar campo hash_id para identificador único
      await queryInterface.addColumn(
        'simulations', 
        'hash_id', 
        {
          type: Sequelize.STRING,
          allowNull: true,
          unique: true
        },
        { transaction }
      );
      
      // Adicionar campo execution_date para registrar a data de início real da simulação
      await queryInterface.addColumn(
        'simulations', 
        'execution_date', 
        {
          type: Sequelize.DATEONLY,
          allowNull: true
        },
        { transaction }
      );
      
      // Adicionar campo is_executed para controlar se o cenário já foi executado
      await queryInterface.addColumn(
        'simulations', 
        'is_executed', 
        {
          type: Sequelize.BOOLEAN,
          defaultValue: false,
          allowNull: false
        },
        { transaction }
      );
    });
  },
  // ...
};

// 2. Rota para executar uma simulação com verificação de status
// backend/src/routes/simulation.js
router.post('/:id/execute', auth, async (req, res) => {
  try {
    const { id } = req.params;
    const { execution_date } = req.body;
    
    // Buscar simulação
    const simulation = await Simulation.findOne({
      where: { id, user_id: req.userId }
    });
    
    if (!simulation) {
      return res.status(404).json({ message: 'Simulação não encontrada.' });
    }
    
    // Verificar se a simulação já foi executada
    if (simulation.is_executed) {
      return res.status(400).json({ 
        message: 'Esta simulação já foi executada. Para executar novamente, crie uma cópia ou edite a simulação.',
        simulation_id: simulation.id,
        hash_id: simulation.hash_id,
        is_executed: true
      });
    }
    
    // Gerar hash_id se não existir
    if (!simulation.hash_id) {
      const timestamp = new Date().getTime();
      const randomPart = Math.random().toString(36).substring(2, 8);
      simulation.hash_id = \`\${randomPart}-\${timestamp}\`;
    }
    
    // Atualizar status e data de execução
    simulation.is_executed = true;
    simulation.execution_date = execution_date || new Date().toISOString().split('T')[0];
    simulation.updated_at = new Date();
    
    await simulation.save();
    
    res.json({
      message: 'Simulação executada com sucesso.',
      simulation: {
        id: simulation.id,
        hash_id: simulation.hash_id,
        execution_date: simulation.execution_date,
        is_executed: simulation.is_executed
      }
    });
  } catch (error) {
    console.error('Erro ao executar simulação:', error);
    res.status(500).json({ message: 'Erro no servidor.' });
  }
});`,
          readmeLink: '/docs/simulation-execution-control',
          date: '2025-05-13',
          authorName: 'Equipe de Desenvolvimento',
          authorRole: 'Desenvolvimento Full Stack',
          tags: ['simulações', 'id-hash', 'execução única', 'clonagem', 'rastreabilidade', 'cenários']
        },
        {
          id: 34,
          title: 'Simulação com Datas Passadas e Cotações de Moedas',
          category: 'simulation',
          topic: 'feature',
          description: 'Implementação de um sistema que permite simular operações financeiras a partir de datas passadas, considerando as cotações diárias de diferentes moedas para calcular resultados de aplicações.',
          problem: 'O sistema anterior não permitia parametrizar simulações com datas passadas nem considerava as variações nas cotações de moedas ao longo do tempo, resultando em previsões imprecisas quando se tratava de operações em diferentes moedas.',
          solution: 'Desenvolvemos um sistema que aceita datas passadas como parâmetro inicial para simulações, integra-se com APIs de cotações de moedas, armazena os resultados das simulações no banco de dados associados às datas e cotações correspondentes, garantindo que cada execução reflita os valores reais das moedas nos dias específicos.',
          codeExample: `// Implementação da consulta à API de cotações de moedas
const getCurrencyExchangeRate = async (date, baseCurrency, targetCurrency) => {
  try {
    // Formatar data no padrão YYYY-MM-DD
    const formattedDate = formatDate(date);
    
    // Consultar API de cotações históricas
    const response = await axios.get(\`\${process.env.EXCHANGE_API_URL}/historical\`, {
      params: {
        date: formattedDate,
        base: baseCurrency,
        symbols: targetCurrency,
        access_key: process.env.EXCHANGE_API_KEY
      }
    });
    
    if (!response.data || !response.data.rates) {
      throw new Error('Não foi possível obter a cotação da moeda.');
    }
    
    return response.data.rates[targetCurrency];
  } catch (error) {
    console.error('Erro ao consultar cotação:', error);
    
    // Em caso de falha, buscar no banco de dados cotações já armazenadas
    const savedRate = await CurrencyRate.findOne({
      where: {
        date: formattedDate,
        base_currency: baseCurrency,
        target_currency: targetCurrency
      }
    });
    
    if (savedRate) {
      return savedRate.rate;
    }
    
    // Se não houver cotação salva, usar a mais recente disponível
    const latestRate = await CurrencyRate.findOne({
      where: {
        base_currency: baseCurrency,
        target_currency: targetCurrency
      },
      order: [['date', 'DESC']]
    });
    
    return latestRate ? latestRate.rate : null;
  }
};

// Implementação do cálculo de remuneração considerando cotações
const calculateSimulationResults = async (simulationId, startDate) => {
  try {
    const simulation = await Simulation.findByPk(simulationId, {
      include: [{ model: InvestmentSetting, as: 'settings' }]
    });
    
    if (!simulation) {
      throw new Error('Simulação não encontrada');
    }
    
    const { initial_amount, leverage_factor, profit_rate, operations_per_day, total_days } = simulation.settings;
    
    // Array para armazenar resultados diários
    const dailyResults = [];
    
    // Calcular para cada dia da simulação
    for (let day = 0; day < total_days; day++) {
      // Calcular a data do dia atual
      const currentDate = addDays(new Date(startDate), day);
      
      // Obter cotações do dia
      const usdToBrl = await getCurrencyExchangeRate(currentDate, 'USD', 'BRL');
      const eurToBrl = await getCurrencyExchangeRate(currentDate, 'EUR', 'BRL');
      const gbpToBrl = await getCurrencyExchangeRate(currentDate, 'GBP', 'BRL');
      
      // Calcular valor total (inicial + alavancado)
      const leveragedAmount = initial_amount * leverage_factor;
      const totalAmount = initial_amount + leveragedAmount;
      
      // Calcular lucro diário considerando a cotação
      const dailyOperations = operations_per_day;
      const dailyProfitRate = profit_rate / 100;
      const dailyProfit = totalAmount * dailyProfitRate * dailyOperations;
      
      // Calcular valores em diferentes moedas
      const profitInBrl = dailyProfit * usdToBrl;
      const profitInEur = dailyProfit / (usdToBrl / eurToBrl);
      const profitInGbp = dailyProfit / (usdToBrl / gbpToBrl);
      
      // Armazenar resultado do dia
      const dailyResult = await DailyOperation.create({
        simulation_id: simulationId,
        day_number: day + 1,
        date: currentDate,
        operations_count: dailyOperations,
        daily_profit: dailyProfit,
        daily_profit_brl: profitInBrl,
        daily_profit_eur: profitInEur,
        daily_profit_gbp: profitInGbp,
        usd_to_brl_rate: usdToBrl,
        eur_to_brl_rate: eurToBrl,
        gbp_to_brl_rate: gbpToBrl
      });
      
      dailyResults.push(dailyResult);
    }
    
    // Atualizar status da simulação
    await simulation.update({
      status: 'completed',
      completed_at: new Date()
    });
    
    return dailyResults;
  } catch (error) {
    console.error('Erro ao calcular resultados da simulação:', error);
    throw error;
  }
};`,
          readmeLink: '#10042025---simulacao-com-datas-passadas-e-cotacoes-de-moedas',
          date: '10/04/2025',
          authorName: 'Equipe de Simulação',
          authorRole: 'Desenvolvedor Full Stack',
          tags: ['simulação', 'cotações', 'moedas', 'api', 'datas', 'histórico', 'câmbio', 'internacionalização', 'dólar', 'euro', 'libra']
        },
        {
          id: 33,
          title: 'Adição de Novos Itens na Knowledge Base',
          category: 'desenvolvimento',
          topic: 'documentação',
          description: 'Este documento descreve como adicionar novos itens na Knowledge Base do sistema Platform Invest 2.0.',
          problem: 'Necessidade de incluir novos conhecimentos e soluções na base de conhecimento do sistema de forma estruturada.',
          solution: 'Adicionar novos objetos no array knowledgeBaseData no arquivo KnowledgeDashboard.js, seguindo a estrutura existente e respeitando o formato JSON esperado.',
          codeExample: `// Exemplo de adição de um novo item no início do array knowledgeBaseData
const knowledgeBaseData = [
  {
    id: XX, // Próximo ID sequencial disponível
    title: "Título do Conhecimento",
    category: "Categoria Relevante",
    topic: "Tópico Específico",
    description: "Descrição detalhada do conhecimento",
    problem: "Problema que este conhecimento resolve",
    solution: "Solução proposta em detalhes",
    codeExample: "Código de exemplo relacionado à solução",
    readmeLink: "/link/para/documentacao/detalhada",
    date: "DD/MM/YYYY",
    authorName: "Nome do Autor",
    authorRole: "Cargo do Autor",
    tags: ["tag1", "tag2", "tag3"]
  },
  // ... itens existentes
];`,
          readmeLink: '#05042025---adição-de-novos-itens-na-knowledge-base',
          date: '05/04/2025',
          authorName: 'Equipe de Desenvolvimento',
          authorRole: 'Desenvolvedor Full Stack',
          tags: ['knowledge-base', 'documentação', 'frontend', 'manutenção']
        },
        {
          id: 32,
          title: 'Implementação de API de Exportação de Dados',
          category: 'backend',
          topic: 'feature',
          description: 'Desenvolvimento de endpoints para exportação de dados em formatos CSV e Excel, com suporte a filtros e personalização de colunas.',
          problem: 'Usuários precisavam extrair dados do sistema para análise externa, sem uma forma padronizada e eficiente de exportar relatórios em formatos como CSV e Excel.',
          solution: 'Criamos uma API completa de exportação com diversos endpoints para diferentes tipos de relatórios, suporte a filtros, paginação, e personalização de colunas a serem exportadas, além de cabeçalhos traduzidos.',
          codeExample: `// Endpoint para exportação de dados no formato CSV/Excel
router.get('/api/export/:reportType', authenticate, async (req, res) => {
  try {
    const { reportType } = req.params;
    const { format = 'csv', filters = {}, columns = [] } = req.query;
    
    // Validar o tipo de relatório
    const validReportTypes = ['operations', 'withdrawals', 'simulations', 'users'];
    if (!validReportTypes.includes(reportType)) {
      return res.status(400).json({ error: 'Tipo de relatório inválido' });
    }
    
    // Obter os dados conforme o tipo de relatório
    const data = await getReportData(reportType, JSON.parse(filters));
    
    // Processar colunas a serem exportadas
    const selectedColumns = columns.length > 0 
      ? JSON.parse(columns) 
      : getDefaultColumns(reportType);
    
    // Exportar no formato solicitado
    if (format.toLowerCase() === 'excel') {
      const workbook = createExcelWorkbook(headers, formattedData);
      res.setHeader('Content-Type', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      res.setHeader('Content-Disposition', \`attachment; filename="\${fileName}.xlsx"\`);
      await workbook.xlsx.write(res);
      res.end();
    } else {
      const csvContent = createCsvContent(headers, formattedData);
      res.setHeader('Content-Type', 'text/csv');
      res.setHeader('Content-Disposition', \`attachment; filename="\${fileName}.csv"\`);
      res.send(csvContent);
    }
  } catch (error) {
    console.error('Erro ao exportar dados:', error);
    res.status(500).json({ error: 'Erro ao processar exportação de dados' });
  }
});`,
          readmeLink: '#01042025---implementacao-de-api-de-exportacao-de-dados',
          date: '01/04/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['api', 'exportação', 'csv', 'excel', 'relatórios', 'dados', 'backend', 'filtros', 'personalização', 'nodejs', 'exceljs']
        },
        {
          id: 23,
          title: 'Reorganização do Menu Lateral e Sub-menus',
          category: 'frontend',
          topic: 'improvement',
          description: 'Reorganização do menu lateral com sub-menus para Configurações do Sistema e adição de links separados para Base de Conhecimento e Dashboard de Conhecimento.',
          problem: 'O menu lateral não possuía organização de links relacionados em sub-menus, o que dificultava a navegação e acesso rápido às funcionalidades do sistema.',
          solution: 'Implementamos um dropdown para Configurações do Sistema que agrupa links relacionados, e adicionamos ícones apropriados para as seções de Base de Conhecimento e Dashboard de Conhecimento.',
          codeExample: `// Menu dropdown de Configurações do Sistema
<li className={\`nav-item dropdown \${
  isActive('/system-settings') || 
  isActive('/admin/email-config') || 
  isActive('/admin/sequelize') || 
  isActive('/admin/error-logs') ? 'active' : ''
}\`}>
  <a 
    href="#" 
    className="nav-link dropdown-toggle" 
    onClick={handleDropdownToggle}
    aria-expanded="false"
  >
    <span className="nav-link-icon d-md-none d-lg-inline-block">
      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </span>
    <span className="nav-link-title">Configurações do Sistema</span>
  </a>
  <div className="dropdown-menu">
    <Link to="/system-settings" className="dropdown-item">
      Configurações Gerais
    </Link>
    <Link to="/admin/email-config" className="dropdown-item">
      Configuração de Email
    </Link>
    <Link to="/admin/sequelize" className="dropdown-item">
      Dashboard do Sequelize
    </Link>
    <Link to="/admin/error-logs" className="dropdown-item">
      Logs de Erros
    </Link>
  </div>
</li>`,
          readmeLink: '#23032025---reorganização-do-menu-lateral-e-sub-menus',
          date: '23/03/2025'
        },
        {
          id: 22,
          title: 'Atualização da Marca e Reorganização do Menu',
          category: 'frontend',
          topic: 'ui-design',
          description: 'Substituição do nome "trabler" para "Simullact.io", reorganização do menu e adição de link para Configurações do Sistema.',
          problem: 'A marca antiga "trabler" não refletia adequadamente o propósito do sistema, e o menu não estava organizado de forma lógica, dificultando o acesso direto às Configurações do Sistema no menu principal.',
          solution: 'Atualizamos a marca para "Simullact.io", uniformizamos as fontes de todo o menu e adicionamos um link direto para Configurações do Sistema.',
          codeExample: `// Atualização do nome da marca no componente MainLayout.js
<h1 className="navbar-brand navbar-brand-autodark">
  <Link to="/">
    <span className="navbar-brand-image" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>Simullact.io</span>
  </Link>
</h1>

// Reorganização do menu com acesso direto às Configurações do Sistema
<li className={\`nav-item \${isActive('/system-settings') ? 'active' : ''}\`}>
  <Link to="/system-settings" className="nav-link">
    <span className="nav-link-icon d-md-none d-lg-inline-block">
      <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    </span>
    <span className="nav-link-title">Configurações do Sistema</span>
  </Link>
</li>`,
          readmeLink: '#22032025---atualização-da-marca-e-reorganização-do-menu',
          date: '22/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['interface', 'menu', 'marca', 'branding', 'navegação', 'redesign', 'usabilidade']
        },
        {
          id: 21,
          title: 'Melhorias Futuras para o Sistema de Documentação',
          category: 'system',
          topic: 'improvement',
          description: 'Propostas de melhorias e expansões futuras para o sistema de documentação detalhada, incluindo recursos adicionais, integrações e aprimoramentos de usabilidade.',
          problem: 'O sistema atual de documentação, embora funcional, pode ser expandido com recursos adicionais para melhorar a colaboração, usabilidade e integração com outras ferramentas de desenvolvimento.',
          solution: 'Implementar gradualmente as melhorias propostas, priorizando aquelas que trarão maior benefício imediato para a equipe de desenvolvimento e usuários do sistema.',
          codeExample: `// Exemplo de implementação de sistema de comentários real
// Model para comentários
const CommentSchema = new mongoose.Schema({
  documentationId: { type: Number, required: true },
  authorId: { type: Number, required: true },
  content: { type: String, required: true },
  parentId: { type: Number, default: null }, // Para respostas em threads
  createdAt: { type: Date, default: Date.now },
  updatedAt: { type: Date, default: Date.now }
});

// API para buscar comentários
router.get('/api/documentation/:id/comments', authenticate, async (req, res) => {
  try {
    const { id } = req.params;
    const comments = await Comment.find({ documentationId: id })
                                 .sort({ createdAt: -1 })
                                 .populate('authorId', 'name role avatar');
    res.json(comments);
  } catch (error) {
    res.status(500).json({ message: 'Erro ao buscar comentários' });
  }
});`,
          readmeLink: '#21032025---melhorias-futuras-para-o-sistema-de-documentação',
          date: '21/03/2025'
        },
        {
          id: 20,
          title: 'Implementação da Página de Documentação Detalhada',
          category: 'frontend',
          topic: 'implementation',
          description: 'Implementação de uma página detalhada de documentação para exibir informações completas sobre itens da base de conhecimento.',
          problem: 'O Painel de Conhecimento exibia apenas informações básicas sobre os itens, sem uma visão detalhada com cronologia, recursos relacionados e outros metadados importantes.',
          solution: 'Criamos uma nova página de documentação detalhada, conectada a uma API de backend, permitindo visualizar informações completas e expandidas sobre cada item da base de conhecimento.',
          codeExample: `// Componente React para exibição de documentação detalhada
const DocumentationPage = () => {
  const { id } = useParams();
  const [documentation, setDocumentation] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get(\`/api/documentation/\${id}\`);
        setDocumentation(response.data);
      } catch (error) {
        console.error('Erro ao buscar documentação:', error);
      }
    };
    
    fetchData();
  }, [id]);
}`,
          readmeLink: '#20032025---implementação-da-página-de-documentação-detalhada',
          date: '20/03/2025'
        },
        {
          id: 19,
          title: 'Correção de Erro de Template Strings Aninhadas no ReadmeChat.js',
          category: 'frontend',
          topic: 'bugfix',
          description: 'Correção de um erro de sintaxe causado por template strings aninhadas no arquivo ReadmeChat.js.',
          problem: 'O erro ocorria devido ao uso de template strings aninhadas sem o devido escape, causando problemas de parsing no JavaScript.',
          solution: 'As template strings aninhadas foram corrigidas escapando os caracteres especiais corretamente.',
          codeExample: `// Exemplo de correção
const viewDockerLogs = (containerName, timestamp) => {
  window.open(\`/admin/docker-logs?container=\${containerName}&timestamp=\${timestamp}\`, '_blank');
};`,
          readmeLink: '#19032025---correção-de-erro-de-template-strings-aninhadas-no-readmechat.js',
          date: '19/03/2025'
        },
        {
          id: 18,
          title: 'Implementação do Sistema de Abas e Quadro Kanban no Painel de Conhecimento',
          category: 'frontend',
          topic: 'implementation',
          description: 'Adição de um sistema de abas no Painel de Conhecimento para alternar entre a visualização da base de conhecimento e uma nova visualização Kanban para gerenciamento de projetos.',
          problem: 'O Painel de Conhecimento exibia apenas as entradas da base de conhecimento, sem oferecer recursos para gestão visual de projetos e tarefas. Além disso, durante a implementação, ocorreu o erro "Module not found: Error: Can\'t resolve \'../components/KanbanBoard\' in \'/app/src/pages\'" porque o componente KanbanBoard necessário não existia no projeto.',
          solution: 'A solução foi implementada em duas etapas principais: atualização do componente KnowledgeDashboard.js para incluir um sistema de abas usando estados React e renderização condicional, e criação do componente KanbanBoard.js para visualização e gerenciamento de tarefas em formato Kanban.',
          codeExample: `// Importar o componente KanbanBoard
import KanbanBoard from '../components/KanbanBoard';

// Adicionar estado para controlar a aba ativa
const [activeTab, setActiveTab] = useState('knowledge');

// Renderização condicional baseada na aba ativa
{activeTab === 'knowledge' ? (
  <div>
    // Conteúdo original da base de conhecimento
  </div>
) : (
  <KanbanBoard />
)}`,
          readmeLink: '#18032025---implementação-do-sistema-de-abas-e-quadro-kanban-no-painel-de-conhecimento',
          date: '18/03/2025'
        },
        {
          id: 17,
          title: 'Correção do Erro 401 ao Carregar Cenários Salvos',
          category: 'authentication',
          topic: 'bugfix',
          description: 'Solução para o erro "Request failed with status code 401" que ocorre ao tentar carregar cenários salvos na página de Configurações.',
          problem: 'Quando o token de autenticação expira, a página de cenários exibe o erro "Houve um problema ao carregar as configurações: Request failed with status code 401. Os valores padrão estão sendo exibidos." A aplicação não estava tratando corretamente esse caso de erro, deixando o usuário sem saber o que fazer.',
          solution: 'Implementamos um tratamento específico para o erro 401 (Unauthorized) que identifica quando o erro é devido a problemas de autenticação, notifica o usuário que sua sessão expirou, limpa os dados da sessão e redireciona automaticamente para a página de login após um breve intervalo.',
          codeExample: `// Tratamento específico para erro 401 (Unauthorized)
if (error.response && error.response.status === 401) {
  console.error('Erro de autenticação: token expirado ou inválido');
  toast.error('Sua sessão expirou. Por favor, faça login novamente.');
  
  // Limpar dados da sessão
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  
  // Redirecionar para login após pequeno delay
  setTimeout(() => {
    window.location.href = '/login';
  }, 2000);
}`,
          readmeLink: '#17032025---correção-do-erro-401-ao-carregar-cenários-salvos',
          date: '17/03/2025'
        },
        {
          id: 16,
          title: 'Melhorias no Modal de Detalhes do Período na Página de Relatórios',
          category: 'frontend',
          topic: 'improvement',
          description: 'Aprimoramento do modal de detalhes do período para exibir informações de forma mais clara e completa.',
          problem: 'O modal de detalhes do período na página de relatórios apresentava limitações na exibição de informações, com valores repetidos, espaçamento inadequado e botões pouco intuitivos.',
          solution: 'Implementamos diversas melhorias: aumentamos o tamanho do modal, adicionamos um card para somatória de remunerações, corrigimos o cálculo do Total Alavancado, melhoramos a visualização das colunas na tabela e substituímos o botão "+" por um ícone mais intuitivo.',
          codeExample: `// 1. Aumento do tamanho do modal
<div className="modal-dialog modal-dialog-centered modal-xl" role="document">

// 2. Adição de card de remuneração total
<div className="col-md-3">
  <div className="card">
    <div className="card-body p-3 text-center">
      <div className="subheader fw-bold">Remuneração</div>
      <div className="h3 text-primary">{formatCurrencyValue(convertCurrency(periodDailyOperations.reduce((sum, op) => sum + (parseFloat(op.daily_withdrawal) || 0), 0)))}</div>
    </div>
  </div>
</div>

// 3. Correção do cálculo do Total Alavancado
<div className="h3 text-azure">{formatCurrencyValue(convertCurrency(periodDailyOperations.reduce((sum, op) => sum + (parseFloat(op.total_profit) || 0), 0)))}</div>`,
          readmeLink: '#16032025---melhorias-no-modal-de-detalhes-do-período-na-página-de-relatórios',
          date: '16/03/2025'
        },
        {
          id: 15,
          title: 'Sistema de Monitoramento de Logs',
          category: 'backend',
          topic: 'feature',
          description: 'Implementação de um sistema completo para visualização e análise de logs de erros e logs do Docker.',
          problem: 'Não havia uma forma centralizada de visualizar e analisar logs de erros do sistema e logs do Docker, dificultando o diagnóstico e resolução de problemas como erros de envio de email.',
          solution: 'Criamos duas páginas dedicadas: uma para logs de erros do sistema e outra para logs do Docker, com funcionalidades de filtragem, exportação e visualização detalhada.',
          codeExample: `// Página de Logs de Erros
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';

const ErrorLogs = () => {
  const [logs, setLogs] = useState({ errors: [], success: [] });
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('errors');
  const [filter, setFilter] = useState('');
  const [selectedError, setSelectedError] = useState(null);

  // Função para visualizar logs do Docker
  const viewDockerLogs = (containerName, timestamp) => {
    window.open(\`/admin/docker-logs?container=\${containerName}&timestamp=\${timestamp}\`, '_blank');
  };
}`,
          readmeLink: '#16032025---implementação-do-sistema-de-monitoramento-de-logs',
          date: '16/03/2025'
        },
        {
          id: 14,
          title: 'Redefinição de Senha do Administrador',
          category: 'authentication',
          topic: 'security',
          description: 'Implementação de sistema para redefinir a senha do administrador quando necessário.',
          problem: 'O administrador esqueceu a senha e não conseguia acessar o sistema, sem haver uma forma fácil de redefinir a senha.',
          solution: 'Criamos um script para atualizar a senha do administrador diretamente no banco de dados e instruções para executá-lo no container Docker.',
          codeExample: `// Script para atualizar a senha do administrador
const bcrypt = require('bcryptjs');
const { User } = require('./backend/models');

async function updateAdminPassword() {
  try {
    const adminUser = await User.findOne({ where: { email: 'admin@example.com' } });
    
    if (!adminUser) {
      console.error('Usuário administrador não encontrado!');
      return;
    }
    
    const salt = await bcrypt.genSalt(10);
    const hashedPassword = await bcrypt.hash('novasenha123', salt);
    
    await adminUser.update({ password: hashedPassword });
    console.log('Senha do administrador atualizada com sucesso!');
  } catch (error) {
    console.error('Erro ao atualizar senha do administrador:', error);
  }
}

updateAdminPassword();`,
          readmeLink: '#15032025---redefinição-de-senha-do-administrador',
          date: '15/03/2025'
        },
        {
          id: 13,
          title: 'Problema de Perda de Histórico de Simulações',
          category: 'database',
          topic: 'bugfix',
          description: 'Correção do problema onde o histórico de simulações era perdido após determinadas ações.',
          problem: 'Usuários estavam perdendo o histórico de simulações após fechar a aplicação ou navegar entre páginas, causando frustração e perda de dados importantes.',
          solution: 'Implementamos persistência adequada no banco de dados e modificamos o componente SimulationHistory para recuperar adequadamente as simulações salvas.',
          codeExample: `// Modificação no componente de histórico
useEffect(() => {
  const fetchSavedSimulations = async () => {
    try {
      const response = await axios.get('/api/simulations/history');
      if (response.data && Array.isArray(response.data)) {
        setSimulations(response.data);
      }
    } catch (error) {
      console.error("Erro ao buscar histórico de simulações:", error);
      toast.error("Não foi possível carregar o histórico de simulações");
    }
  };

  fetchSavedSimulations();
}, []);`,
          readmeLink: '#14032025---correção-do-problema-de-perda-de-histórico-de-simulações',
          date: '14/03/2025'
        },
        {
          id: 12,
          title: 'Melhoria na Exibição do Período de Operação das Simulações',
          category: 'frontend',
          topic: 'improvement',
          description: 'Implementação de melhorias na forma como os períodos de operação são exibidos nas simulações.',
          problem: 'Os períodos de operação das simulações eram apresentados de forma confusa, dificultando a compreensão pelos usuários.',
          solution: 'Melhoramos a exibição com formatação de data mais clara, adição de tooltips explicativos e reorganização das informações nas páginas de relatórios e configurações.',
          codeExample: `// Formatação de data melhorada
const formatSimpleDate = (dateString) => {
  const date = new Date(dateString);
  return \`\${date.getDate().toString().padStart(2, '0')}/\${(date.getMonth() + 1).toString().padStart(2, '0')}/\${date.getFullYear().toString().slice(2)}\`;
};

// Utilizando a formatação nos relatórios
<div className="period-info">
  <span title="Data de início da simulação">
    <i className="fas fa-calendar-alt me-1"></i>
    {formatSimpleDate(simulation.start_date)}
  </span>
  <span className="mx-2">→</span>
  <span title="Data de término da simulação">
    <i className="fas fa-calendar-check me-1"></i>
    {formatSimpleDate(simulation.end_date)}
  </span>
</div>`,
          readmeLink: '#14032025---melhoria-na-exibição-do-período-de-operação-das-simulações',
          date: '14/03/2025'
        },
        {
          id: 11,
          title: 'Implementação do Dashboard do Sequelize e Menu de Administração',
          category: 'backend',
          topic: 'feature',
          description: 'Criação de um dashboard para visualizar e gerenciar modelos do Sequelize e implementação de um menu de administração.',
          problem: 'Não havia uma interface para os administradores visualizarem e gerenciarem os modelos e tabelas do banco de dados de forma fácil.',
          solution: 'Implementamos um dashboard completo do Sequelize para visualizar modelos, associações e dados, além de um menu de administração para acesso a ferramentas administrativas.',
          codeExample: `// Rota backend para listar modelos do Sequelize
app.get('/api/admin/sequelize/models', authenticateAdmin, async (req, res) => {
  try {
    // Lista todos os modelos disponíveis no Sequelize
    const db = require('../models');
    const models = Object.keys(db).filter(key => key !== 'sequelize' && key !== 'Sequelize')
      .map(modelName => {
        const model = db[modelName];
        return {
          name: modelName,
          tableName: model.tableName,
          attributes: Object.keys(model.rawAttributes).map(attr => ({
            name: attr,
            type: model.rawAttributes[attr].type.key,
            allowNull: model.rawAttributes[attr].allowNull || false,
            primaryKey: model.rawAttributes[attr].primaryKey || false
          })),
          associations: Object.keys(model.associations).map(assocName => ({
            name: assocName,
            type: model.associations[assocName].associationType,
            target: model.associations[assocName].target.name
          }))
        };
      });
    
    res.json(models);
  } catch (error) {
    console.error('Erro ao listar modelos do Sequelize:', error);
    res.status(500).json({ message: 'Erro no servidor.' });
  }
});`,
          readmeLink: '#14032025---implementação-do-dashboard-do-sequelize-e-menu-de-administração',
          date: '14/03/2025'
        },
        {
          id: 10,
          title: 'Correção do Erro de Coluna Ausente na Tabela Operations',
          category: 'database',
          topic: 'bugfix',
          description: 'Correção do erro causado pela ausência da coluna "period" na tabela de operações.',
          problem: 'A aplicação apresentava erros ao tentar acessar a coluna "period" nas operações, que não existia no banco de dados.',
          solution: 'Criamos e executamos uma migração para adicionar a coluna "period" à tabela operations, com valor padrão de 1.',
          codeExample: `// Migração para adicionar a coluna 'period'
module.exports = {
  up: async (queryInterface, Sequelize) => {
    await queryInterface.addColumn('operations', 'period', {
      type: Sequelize.INTEGER,
      allowNull: false,
      defaultValue: 1
    });
  },

  down: async (queryInterface, Sequelize) => {
    await queryInterface.removeColumn('operations', 'period');
  }
};

// Como executar a migração:
// 1. Salve o arquivo como 'migrations/20250313000000-add-period-to-operations.js'
// 2. Execute o comando: npx sequelize-cli db:migrate`,
          readmeLink: '#13032025---correção-do-erro-de-coluna-ausente-na-tabela-operations',
          date: '13/03/2025'
        },
        {
          id: 9,
          title: 'Correção do problema de sobreposição do menu de administração',
          category: 'frontend',
          topic: 'bugfix',
          description: 'O menu dropdown de administração estava sendo sobreposto pelo conteúdo principal da aplicação, tornando impossível acessar as opções do menu.',
          problem: 'O menu dropdown de administração na barra lateral estava sendo sobreposto pelo conteúdo principal, impedindo o acesso às opções como "Configuração de Email" e "Dashboard do Sequelize".',
          solution: 'Movemos o menu de administração da barra lateral para o canto superior direito do cabeçalho, junto com o perfil do usuário, e ajustamos o CSS para garantir o posicionamento correto.',
          codeExample: `// Ajuste no HTML/JSX
<div className="navbar-nav flex-row order-md-last ms-auto">
  {isAdmin && (
    <div className="nav-item dropdown me-3">
      <a href="#" className="nav-link dropdown-toggle" onClick={handleDropdownToggle}>
        <span className="nav-link-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" />
          </svg>
        </span>
        <span className="d-none d-md-inline">Administração</span>
      </a>
      <div className="dropdown-menu dropdown-menu-end">
        <Link to="/admin/email-config" className="dropdown-item">
          Configuração de Email
        </Link>
        <Link to="/admin/sequelize" className="dropdown-item">
          Dashboard do Sequelize
        </Link>
      </div>
    </div>
  )}
</div>`,
          readmeLink: '#15032025---correção-do-problema-de-sobreposição-do-menu-de-administração',
          date: '15/03/2025'
        },
        {
          id: 8,
          title: 'Erro "prevLogs[type] is not iterable"',
          category: 'frontend',
          topic: 'error',
          description: 'Erro ao acessar a página de resultados da simulação gerada e clicar no botão "Ver Detalhes".',
          problem: 'O erro ocorria na função `addToLog` do componente `Reports.js`, onde o código tentava usar o operador spread (`...`) em `prevLogs[type]`, mas essa propriedade não era um array em algumas situações.',
          solution: 'Modificamos a função `addToLog` para garantir que `prevLogs[type]` seja sempre tratado como um array.',
          codeExample: `const addToLog = (type, message) => {
  const logEntry = {
    timestamp: new Date(),
    message,
    details: typeof message === 'object' ? JSON.stringify(message) : message
  };
  
  setLogs(prevLogs => {
    // Garantir que prevLogs seja um objeto válido
    const safeObj = prevLogs || {};
    
    // Garantir que prevLogs[type] seja um array
    const existingLogs = Array.isArray(safeObj[type]) ? safeObj[type] : [];
    
    return {
      ...safeObj,
      [type]: [logEntry, ...existingLogs].slice(0, 100)
    };
  });
};`,
          readmeLink: '#14032025---correção-do-erro-prevlogstype-is-not-iterable',
          date: '14/03/2025'
        },
        {
          id: 24,
          title: 'Implementação da Página de Documentação Detalhada com Sistema de Comentários',
          category: 'frontend',
          topic: 'implementation',
          description: 'Desenvolvimento de uma página completa para visualização detalhada de documentação, incluindo sistema de comentários, linha do tempo, recursos relacionados e mais.',
          problem: 'A visualização da documentação estava limitada ao painel principal, sem possibilidade de ver detalhes completos, histórico de desenvolvimento e interagir através de comentários.',
          solution: 'Implementamos uma página dedicada para visualização detalhada da documentação, com seções organizadas para diferentes tipos de informação e um sistema completo de comentários.',
          codeExample: `// Componente React para exibição de documentação detalhada
const DocumentationPage = () => {
  const { id } = useParams();
  const [documentation, setDocumentation] = useState(null);
  const [comments, setComments] = useState([]);
  
  useEffect(() => {
    const fetchDocumentation = async () => {
      try {
        const response = await axiosInstance.get(\`/api/documentation/\${id}\`);
        setDocumentation(response.data);
      } catch (error) {
        console.error('Erro ao carregar documentação:', error);
        toast.error('Não foi possível carregar a documentação.');
      }
    };
    
    if (id) {
      fetchDocumentation();
    }
  }, [id]);
}`,
          readmeLink: '#24032025---implementação-da-página-de-documentação-detalhada-com-sistema-de-comentários',
          date: '24/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['react', 'documentação', 'frontend', 'ui/ux', 'comentários', 'timeline', 'desenvolvimento']
        },
        {
          id: 26,
          title: 'Reconstrução da Documentação Detalhada para Todos os IDs',
          category: 'documentation',
          topic: 'improvement',
          description: 'Processo de refatoração e reconstrução da documentação detalhada para garantir que todos os itens da base de conhecimento (IDs 10-26) tenham páginas completas e funcionais.',
          problem: 'As páginas de documentação detalhada estavam usando um template genérico para a maioria dos IDs, com exceção do ID 19, resultando em informações incompletas e uma experiência do usuário inconsistente.',
          solution: 'Implementamos uma refatoração completa do componente DocumentationPage.js para carregar dados reais de todos os itens, além de corrigir a integração com a API de documentação e garantir que os seeders estejam devidamente configurados.',
          codeExample: `// Melhoria no componente DocumentationPage.js para buscar dados reais da API
useEffect(() => {
  const fetchDocumentation = async () => {
    setLoading(true);
    try {
      // Tentativa de buscar da API real
      const response = await axiosInstance.get(\`/api/documentation/\${id}\`);
      if (response.data && Object.keys(response.data).length > 0) {
        setDocumentation(response.data);
      } else {
        // Fallback para dados locais em caso de API indisponível
        const localDoc = knowledgeBaseData.find(item => item.id.toString() === id);
        if (localDoc) {
          setDocumentation(localDoc);
        } else {
          toast.error('Documentação não encontrada');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar documentação:', error);
      
      // Fallback para dados locais em caso de erro
      const localDoc = knowledgeBaseData.find(item => item.id.toString() === id);
      if (localDoc) {
        setDocumentation(localDoc);
        toast.warning('Usando dados locais devido a um erro de API');
      } else {
        toast.error('Documentação não encontrada');
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (id) {
    fetchDocumentation();
  }
}, [id]);`,
          readmeLink: '#26032025---reconstrução-da-documentação-detalhada-para-todos-os-ids',
          date: '26/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['documentação', 'refatoração', 'api', 'frontend', 'react', 'sequelize', 'seeders', 'usabilidade', 'experiência do usuário']
        },
        {
          id: 25,
          title: 'Erro de Procedimento: Falha na Reconstrução do Frontend após Alterações',
          category: 'devops',
          topic: 'error',
          description: 'Análise detalhada sobre o erro de procedimento na reconstrução do frontend após alterações em arquivos JavaScript que resultou em atrasos na visualização das mudanças.',
          problem: 'Após modificações nos arquivos do frontend, especificamente na adição do item 24 ao KnowledgeDashboard, o processo padrão de reconstrução do container não foi seguido, resultando em inconsistências na visualização.',
          solution: 'Retorno à sequência correta de procedimentos: 1) Modificar arquivos, 2) Reconstruir o frontend com docker-compose build frontend, 3) Reiniciar o serviço com docker-compose restart frontend.',
          codeExample: `# Sequência correta de comandos após alterações no frontend
# 1. Alterar os arquivos necessários
# 2. Reconstruir o container
docker-compose build frontend

# 3. Reiniciar o serviço
docker-compose restart frontend`,
          readmeLink: '#25032025---erro-de-procedimento-falha-na-reconstrução-do-frontend',
          date: '25/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['devops', 'erro-procedimento', 'docker', 'reconstrução', 'frontend', 'docker-compose', 'workflow', 'automação']
        },
        {
          id: 29,
          title: 'Correção Completa de Referências a knowledgeBaseData no DocumentationPage.js',
          category: 'frontend', 
          topic: 'bugfix',
          description: 'Resolução definitiva de múltiplos erros de referência indefinida a knowledgeBaseData em diferentes blocos do código no componente DocumentationPage.js.',
          problem: 'Após a correção inicial do erro de knowledgeBaseData no bloco try, outro erro similar foi descoberto na linha 539 dentro do bloco catch, resultando no erro [eslint] src/pages/DocumentationPage.js Line 539:26: \'knowledgeBaseData\' is not defined no-undef.',
          solution: 'Reestruturamos completamente o componente, movendo a declaração da variável knowledgeBaseData para o escopo principal do useEffect, garantindo que esteja disponível em todos os blocos de código que precisam acessá-la, incluindo os blocos try e catch.',
          codeExample: `// Solução completa:
useEffect(() => {
  // Definindo knowledgeBaseData no escopo principal do useEffect
  // para que esteja disponível em qualquer lugar dentro dele
  const knowledgeBaseData = [
    { id: 19, /* ... dados completos ... */ },
    { id: 24, /* ... dados completos ... */ },
    { id: 25, /* ... dados completos ... */ },
    { id: 26, /* ... dados completos ... */ },
    { id: 27, /* ... dados completos ... */ },
    { id: 28, /* ... dados completos ... */ }
  ];
  
  const fetchDocumentation = async () => {
    try {
      // Dentro do try, knowledgeBaseData está disponível
      // ...
    } catch (error) {
      // Dentro do catch, knowledgeBaseData também está disponível
      const localDoc = knowledgeBaseData.find(item => item.id.toString() === id);
      // ...
    }
  };
  
  // ... restante do código
}, [id]);`,
          readmeLink: '#29032025---correção-completa-de-referências-a-knowledgebasedata-no-documentationpagejs',
          date: '29/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['frontend', 'react', 'bugfix', 'escopo-de-variáveis', 'useEffect', 'error-handling', 'refatoração', 'eslint', 'documentação']
        },
        {
          id: 28,
          title: 'Correção de Erro: Referência Indefinida a knowledgeBaseData',
          category: 'frontend',
          topic: 'bugfix',
          description: 'Resolução definitiva de múltiplos erros de referência indefinida a knowledgeBaseData em diferentes blocos do código no componente DocumentationPage.js.',
          problem: 'Após a correção inicial do erro de knowledgeBaseData no bloco try, outro erro similar foi descoberto na linha 539 dentro do bloco catch, resultando no erro [eslint] src/pages/DocumentationPage.js Line 539:26: \'knowledgeBaseData\' is not defined no-undef.',
          solution: 'Reestruturamos completamente o componente, movendo a declaração da variável knowledgeBaseData para o escopo principal do useEffect, garantindo que esteja disponível em todos os blocos de código que precisam acessá-la, incluindo os blocos try e catch.',
          codeExample: `// Solução completa:
useEffect(() => {
  // Definindo knowledgeBaseData no escopo principal do useEffect
  // para que esteja disponível em qualquer lugar dentro dele
  const knowledgeBaseData = [
    { id: 19, /* ... dados completos ... */ },
    { id: 24, /* ... dados completos ... */ },
    { id: 25, /* ... dados completos ... */ },
    { id: 26, /* ... dados completos ... */ },
    { id: 27, /* ... dados completos ... */ },
    { id: 28, /* ... dados completos ... */ }
  ];
  
  const fetchDocumentation = async () => {
    try {
      // Dentro do try, knowledgeBaseData está disponível
      // ...
    } catch (error) {
      // Dentro do catch, knowledgeBaseData também está disponível
      const localDoc = knowledgeBaseData.find(item => item.id.toString() === id);
      // ...
    }
  };
  
  // ... restante do código
}, [id]);`,
          readmeLink: '#28032025---correção-de-erro-referência-indefinida-a-knowledgebasedata',
          date: '28/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['frontend', 'react', 'bugfix', 'escopo-de-variáveis', 'useEffect', 'error-handling', 'refatoração', 'eslint', 'documentação']
        },
        {
          id: 27,
          title: 'Correção da Leitura de Documentação e Centralização dos Dados',
          category: 'bugfix',
          topic: 'maintenance',
          description: 'Correção de um problema de leitura da documentação detalhada e implementação de uma abordagem centralizada para armazenamento dos dados de documentação no frontend.',
          problem: 'A refatoração anterior do componente DocumentationPage.js resultou em um erro onde a variável knowledgeBaseData foi removida sem substituição adequada, causando falhas na leitura de documentação e exibição apenas do template genérico.',
          solution: 'Reimplementamos a variável knowledgeBaseData dentro do componente, incluindo todos os itens necessários à variável.',
          codeExample: `// Erro original:
try {
  // Dados da API...
} catch (error) {
  // ERRO: knowledgeBaseData não está definido aqui
  const localDoc = knowledgeBaseData.find(item => item.id.toString() === id);
  // ...
}

// Solução:
try {
  // Definir knowledgeBaseData no mesmo escopo onde é utilizado
  const knowledgeBaseData = [
    { id: 19, /* dados completos */ },
    { id: 24, /* dados completos */ },
    { id: 25, /* dados completos */ },
    { id: 26, /* dados completos */ },
    { id: 27, /* dados completos */ }
  ];
  
  // Dados da API...
    } catch (error) {
  // Agora knowledgeBaseData está disponível aqui
  const localDoc = knowledgeBaseData.find(item => item.id.toString() === id);
  // ...
}`,
          readmeLink: '#27032025---correção-da-leitura-de-documentação-e-centralização-dos-dados',
          date: '27/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['bugfix', 'documentação', 'frontend', 'react', 'cache', 'manutenção', 'dados locais', 'fallback', 'erro']
        },
        {
          id: 30,
          title: 'Otimização de Desempenho da Página de Documentação',
          category: 'frontend',
          topic: 'performance',
          description: 'Implementação de melhorias de desempenho e cache para a página de documentação, incluindo tratamento avançado de erros de API.',
          problem: 'Usuários estavam enfrentando tempos de carregamento lentos e notificações de erro frequentes ao acessar páginas de documentação devido a ineficiências no carregamento de dados e tratamento inadequado de erros de API.',
          solution: 'Implementamos um sistema de cache local avançado, otimizamos o carregamento de dados, e melhoramos o tratamento de erros para proporcionar uma experiência de usuário mais suave, mesmo quando a API estiver indisponível.',
          codeExample: `// Implementação de sistema de cache avançado
const CACHE_DURATION = 3600000; // 1 hora em milissegundos

// Função para salvar dados em cache com timestamp
const saveToCache = (key, data) => {
  const cacheItem = {
    data,
    timestamp: Date.now()
  };
  localStorage.setItem(key, JSON.stringify(cacheItem));
};

// Uso no componente DocumentationPage
useEffect(() => {
  const fetchDocumentation = async () => {
    // Verificar cache primeiro
    const cacheKey = \`documentation_\${id}\`;
    const cachedData = getFromCache(cacheKey);
    
    if (cachedData) {
      console.log('Usando dados em cache para documentação', id);
      setDocumentation(cachedData);
      setLoading(false);
      return;
    }
    
    // Continua com a busca na API se não tiver em cache
    // ...
  };
}); `,
          readmeLink: '#30032025---otimização-de-desempenho-da-página-de-documentação',
          date: '30/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['performance', 'cache', 'frontend', 'react', 'api', 'otimização', 'experiência-do-usuário', 'offline', 'tratamento-de-erros']
        },
        {
          id: 31,
          title: 'Diagnóstico e Monitoramento de Erros de API',
          category: 'backend',
          topic: 'monitoring',
          description: 'Implementação de um sistema completo para diagnóstico, monitoramento e registro de erros de API, incluindo um painel de controle dedicado para visualização de logs de erros.',
          problem: 'Erros na API não estavam sendo devidamente registrados, monitorados ou diagnosticados, resultando em falhas silenciosas, dificuldade na identificação de problemas e degradação da experiência do usuário.',
          solution: 'Implementamos um sistema abrangente de registro e monitoramento de erros de API que inclui captura detalhada de informações, sistema de alertas, painel de visualização de logs e estratégias de fallback para garantir a continuidade do serviço mesmo em caso de falhas.',
          codeExample: `// Middleware de captura e registro de erros de API
const errorLoggerMiddleware = (err, req, res, next) => {
  // Extrair informações contextuais
  const error = {
    timestamp: new Date(),
    path: req.path,
    method: req.method,
    ip: req.ip,
    error: {
      name: err.name,
      message: err.message,
      stack: process.env.NODE_ENV === 'production' ? undefined : err.stack,
      status: err.status || 500
    },
    request: {
      headers: req.headers,
      query: req.query,
      params: req.params,
      body: req.method !== 'GET' ? req.body : undefined,
      auth: req.user ? { id: req.user.id, role: req.user.role } : null
    }
  };

  // Registrar no banco de dados
  ErrorLog.create({
    type: 'api_error',
    severity: err.status >= 500 ? 'critical' : 'warning',
    message: err.message,
    details: JSON.stringify(error),
    path: req.path,
    method: req.method,
    ip: req.ip,
    user_id: req.user ? req.user.id : null,
    stack: process.env.NODE_ENV === 'production' ? null : err.stack,
    status_code: err.status || 500,
    resolved: false
  })
  .then(log => {
    console.error(\`[API-ERROR-\${log.id}] \${err.message} - Path: \${req.path}\`);
    
    // Enviar alerta se for erro crítico
    if (err.status >= 500) {
      notifyAdmins(\`Erro crítico na API: \${err.message}\`, log.id);
    }
    
    // Adicionar ID do log à resposta para rastreamento
    res.status(err.status || 500).json({
      error: {
        message: err.message,
        logId: log.id
      }
    });
  })
  .catch(logError => {
    console.error('Erro ao registrar erro da API:', logError);
    next(err);
  });
};

// Uso como middleware global
app.use(errorLoggerMiddleware);`,
          readmeLink: '#31032025---diagnostico-e-monitoramento-de-erros-de-api',
          date: '31/03/2025',
          authorName: 'Admin',
          authorRole: 'Administrador do Sistema',
          tags: ['api', 'monitoramento', 'logs', 'erros', 'diagnóstico', 'middleware', 'express', 'notificações', 'alerta', 'resiliência', 'fallback', 'experiência do usuário']
        }
      ];
      
      setKnowledgeData(knowledgeBaseData);
      setLoading(false);
    }, 1000);
  }, []);

  // Filtrar dados com base na pesquisa e filtros
  const filteredData = knowledgeData.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesTopic = selectedTopic === 'all' || item.topic === selectedTopic;
    
    return matchesSearch && matchesCategory && matchesTopic;
  });

  // Função para renderizar o conteúdo principal
  const renderMainContent = () => {
    if (activeTab === 'knowledge') {
      if (loading) {
        return (
          <div className="empty">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3">Carregando base de conhecimento...</p>
          </div>
        );
      }
      
      if (filteredData.length === 0) {
        return (
          <div className="empty">
            <div className="empty-img">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search-off" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M5.039 5.062a7 7 0 0 0 9.91 9.89m1.584 -2.434a7 7 0 0 0 -9.038 -9.057"></path>
                <path d="M3 3l18 18"></path>
              </svg>
            </div>
            <p className="empty-title">Nenhum resultado encontrado</p>
            <p className="empty-subtitle text-muted">
              Tente ajustar seus filtros ou termos de pesquisa.
            </p>
          </div>
        );
      }
      
      return (
        <div className="row row-cards">
          {filteredData.map(item => (
            <div key={item.id} className="col-md-6 col-xl-4">
              <div className="card">
                <div className="card-status-top bg-primary"></div>
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <span className={`badge bg-${getCategoryColor(item.category)} me-2`}>{item.category}</span>
                    <span className={`badge bg-${getTopicColor(item.topic)}`}>{item.topic}</span>
                    <div className="ms-auto text-end">
                      <div className="text-muted">{item.date}</div>
                      <div className="fw-bold">ID: {item.id}</div>
                    </div>
                  </div>
                  <h3 className="card-title">{item.title}</h3>
                  <p className="text-muted">{item.description}</p>
                  <div className="d-flex gap-2">
                    <Link to={`/admin/documentation/${item.id}`} className="btn btn-primary flex-grow-1">
                      <i className="fas fa-file-alt me-2"></i> Ver documentação detalhada
                    </Link>
                    <Link to={`/readme-chat${item.readmeLink}`} className="btn btn-outline-secondary">
                      <i className="fas fa-code me-2"></i> Ver no ReadmeChat
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      return <KanbanBoard />;
    }
  };

  return (
    <MainLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">
                Painel de Conhecimento
              </h2>
              <div className="text-muted mt-1">Gerencie e explore a base de conhecimento do sistema</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <button className="btn btn-primary d-none d-sm-inline-block" onClick={() => setShowAddModal(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Adicionar Item
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <a 
                    href="#" 
                    className={`nav-link ${activeTab === 'knowledge' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('knowledge');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
                      <path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
                      <line x1="3" y1="6" x2="3" y2="19" />
                      <line x1="12" y1="6" x2="12" y2="19" />
                      <line x1="21" y1="6" x2="21" y2="19" />
                    </svg>
                    Base de Conhecimento
                  </a>
                </li>
                <li className="nav-item">
                  <a 
                    href="#" 
                    className={`nav-link ${activeTab === 'kanban' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('kanban');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <line x1="4" y1="4" x2="10" y2="4" />
                      <line x1="14" y1="4" x2="20" y2="4" />
                      <rect x="4" y="8" width="6" height="12" rx="2" />
                      <rect x="14" y="8" width="6" height="6" rx="2" />
                    </svg>
                    Kanban
                  </a>
                </li>
              </ul>
              <div className="card-actions">
                <div className="row g-2 align-items-center">
                  <div className="col">
                    <div className="input-icon">
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Pesquisar..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="input-icon-addon">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <circle cx="10" cy="10" r="7" />
                          <line x1="21" y1="21" x2="15" y2="15" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="col-auto">
                    <select 
                      className="form-select" 
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">Todas as Categorias</option>
                      <option value="frontend">Frontend</option>
                      <option value="backend">Backend</option>
                      <option value="database">Banco de Dados</option>
                      <option value="authentication">Autenticação</option>
                      <option value="simulation">Simulação</option>
                      <option value="development">Desenvolvimento</option>
                      <option value="docker">Docker</option>
                      <option value="system">Sistema</option>
                    </select>
                  </div>
                  <div className="col-auto">
                    <select 
                      className="form-select" 
                      value={selectedTopic}
                      onChange={(e) => setSelectedTopic(e.target.value)}
                    >
                      <option value="all">Todos os Tópicos</option>
                      <option value="feature">Funcionalidade</option>
                      <option value="bugfix">Correção de Bug</option>
                      <option value="improvement">Melhoria</option>
                      <option value="documentation">Documentação</option>
                      <option value="error">Erro</option>
                      <option value="implementation">Implementação</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              {renderMainContent()}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

// Funções auxiliares para cores
function getCategoryColor(category) {
  switch (category) {
    case 'frontend':
      return 'primary';
    case 'backend':
      return 'success';
    case 'database':
      return 'info';
    case 'authentication':
      return 'warning';
    case 'simulation':
      return 'danger';
    case 'reports':
      return 'secondary';
    case 'configuration':
      return 'dark';
    case 'docker':
      return 'warning';
    case 'system':
      return 'success';
    case 'desenvolvimento':
      return 'primary';
    default:
      return 'muted';
  }
}

function getTopicColor(topic) {
  switch (topic) {
    case 'error':
      return 'danger';
    case 'feature':
      return 'success';
    case 'improvement':
      return 'info';
    case 'bugfix':
      return 'warning';
    case 'implementation':
      return 'success';
    case 'documentation':
    case 'documentação':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export default KnowledgeDashboard; 
